import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FileText, Download, Trash2 } from "lucide-react";
import { BUSINESS_SNAPSHOT_QUESTIONS } from "../../lib/businessSnapshotQA";
import { ClientDocumentUpload, RM_DOC_LABEL } from "./ClientDocumentUpload";
import { RunAnalysisPanel } from "./RunAnalysisPanel";

const REVENUE_STAGE_LABELS = {
  pre_revenue: "Pre-revenue (KES 0 – 10M p.a.)",
  early_revenue: "Early revenue (KES 10M – 50M p.a.)",
  growth: "Growth (over KES 50M p.a.)",
};
const QUIZ_STAGE_LABELS = { pre_revenue: "Pre-revenue", post_revenue: "Post-revenue" };
const QUIZ_BAND_LABELS = {
  lt_100k: "Less than KES 100K / month", "100k_1m": "KES 100K – 1M / month",
  "1m_5m": "KES 1M – 5M / month", "5m_15m": "KES 5M – 15M / month", gt_15m: "KES 15M+ / month",
};
const QUIZ_PROFIT_LABELS = { yes: "Yes", no: "No", not_sure: "Not sure" };
const QUIZ_DOC_LABELS = {
  pitch_deck: "Pitch deck", mgmt_accounts: "Management accounts", financial_model: "Financial model",
  audited_financials: "Audited financials", certificate: "Company certificate",
};

const Row = ({ k, v, testId }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="shrink-0 text-slate-500">{k}</dt>
    <dd className="truncate text-right font-medium text-slate-800" data-testid={testId}>{v || "—"}</dd>
  </div>
);
const Heading = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A8B85]">{children}</p>
);

export const ClientProfilePanel = ({ companyId, clientName, client, onRefresh }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/rm/clients/${companyId}/profile`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load client profile"));
  }, [companyId]);

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/rm/clients/${companyId}/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data], { type: doc.content_type || "application/octet-stream" }));
      a.download = doc.filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Couldn't download document"); }
  };

  const deleteDoc = async (doc) => {
    const who = doc.uploaded_by_role === "rm" ? `uploaded by ${doc.uploaded_by_name || "an RM"}` : "uploaded by the founder";
    if (!window.confirm(`Delete "${doc.filename}" (${who}) from ${clientName || "the client"}'s Data Room?\n\nThis removes it from the client's profile and the next analysis. It cannot be undone.`)) return;
    try {
      await api.delete(`/rm/clients/${companyId}/documents/${doc.doc_id}`);
      setData((prev) => ({ ...prev, documents: (prev?.documents || []).filter((d) => d.doc_id !== doc.doc_id) }));
      toast.success("Document deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete document"); }
  };

  if (err) return <p className="mt-3 text-xs text-red-600" data-testid={`rm-profile-error-${companyId}`}>{err}</p>;
  if (!data) return <p className="mt-3 text-xs text-slate-400">Loading profile…</p>;
  const { sme, documents } = data;
  const quiz = sme.get_started_quiz;
  const qa = sme.business_snapshot_qa;
  const hasQa = qa && Object.values(qa.answers || {}).some(Boolean);

  return (
    <div className="mt-3 space-y-3" data-testid={`rm-profile-panel-${companyId}`}>
      <RunAnalysisPanel companyId={companyId} clientName={clientName} companyName={client?.company_name}
        crsScore={client?.crs_score} rmReportGeneratedAt={client?.rm_report_generated_at}
        rmReportGeneratedBy={client?.rm_report_generated_by} onDone={onRefresh} />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-extrabold text-slate-900">Founder Snapshot &amp; Quiz</p>
        <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 text-xs">
          <div>
            <Heading>Business snapshot</Heading>
            <dl className="mt-2 space-y-1.5">
              <Row k="Revenue stage" v={REVENUE_STAGE_LABELS[sme.revenue_stage] || sme.revenue_stage} testId={`rm-profile-revenue-stage-${companyId}`} />
              <Row k="Industry" v={sme.sector} />
              <Row k="Country" v={sme.country} />
              <Row k="Founder role" v={sme.founder_role} />
              <Row k="Website" v={sme.website} />
              <Row k="LinkedIn" v={sme.linkedin_url} />
              <div>
                <dt className="text-slate-500">Business summary</dt>
                <dd className="mt-1 whitespace-pre-line text-slate-700" data-testid={`rm-profile-summary-${companyId}`}>{sme.business_summary || "—"}</dd>
              </div>
            </dl>
          </div>
          <div>
            <Heading>Get Started quiz answers</Heading>
            {quiz ? (
              <dl className="mt-2 space-y-1.5" data-testid={`rm-profile-quiz-${companyId}`}>
                <Row k="Stage selected" v={QUIZ_STAGE_LABELS[quiz.stage] || quiz.stage} />
                <Row k="Monthly revenue band" v={QUIZ_BAND_LABELS[quiz.revenue_band] || quiz.revenue_band} />
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-slate-500">Profitable?</dt>
                  <dd><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                    quiz.is_profitable === "yes" ? "bg-emerald-50 text-emerald-700" : quiz.is_profitable === "no" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                    {QUIZ_PROFIT_LABELS[quiz.is_profitable] || "—"}</span></dd>
                </div>
                <div>
                  <dt className="text-slate-500">Documents they said they have</dt>
                  <dd className="mt-1.5 flex flex-wrap gap-1.5">
                    {(quiz.docs || []).length === 0 ? <span className="text-slate-400">None ticked</span>
                      : quiz.docs.map((d) => <span key={d} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">{QUIZ_DOC_LABELS[d] || d}</span>)}
                  </dd>
                </div>
              </dl>
            ) : <p className="mt-2 text-slate-400">Not available — this founder skipped the quiz or signed up before it existed.</p>}
          </div>
        </div>
        {hasQa && (
          <div className="mt-4 border-t border-slate-100 pt-3 text-xs" data-testid={`rm-profile-qa-${companyId}`}>
            <Heading>Business Snapshot Q&amp;A (no pitch deck)</Heading>
            <dl className="mt-2 space-y-3">
              {BUSINESS_SNAPSHOT_QUESTIONS.map((q, i) => qa.answers?.[q.key] ? (
                <div key={q.key}>
                  <dt className="font-semibold text-slate-500">{i + 1}. {q.label}</dt>
                  <dd className="mt-0.5 whitespace-pre-line text-slate-700">{qa.answers[q.key]}</dd>
                </div>
              ) : null)}
            </dl>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`rm-profile-docs-${companyId}`}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-extrabold text-slate-900">Documents</p>
          <span className="text-[11px] font-semibold text-slate-500">{documents.length} on file</span>
        </div>
        <p className="mt-0.5 text-[11px] text-slate-500">Everything in the client&apos;s Data Room — uploaded by the founder or by you. Download to review, or delete anything that is wrong or out of date (deletions are logged).</p>
        {documents.length === 0 ? (
          <p className="mt-3 text-xs text-slate-400">No documents on file yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100">
            {documents.map((d) => (
              <li key={d.doc_id} className="flex items-center justify-between gap-3 py-2 text-xs" data-testid={`rm-doc-${d.doc_id}`}>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <FileText size={14} className="shrink-0 text-slate-400" />
                  <span className="truncate font-medium text-slate-800">{d.filename}</span>
                  {d.size ? <span className="shrink-0 text-slate-400">({Math.round(d.size / 1024)}KB)</span> : null}
                  {d.doc_type && <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{RM_DOC_LABEL[d.doc_type] || d.doc_type.replace(/_/g, " ")}</span>}
                  {d.uploaded_by_role === "rm" ? (
                    <span className="shrink-0 rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]" data-testid={`rm-doc-by-rm-${d.doc_id}`}>
                      Uploaded by {d.uploaded_by_name || "RM"} · {(d.uploaded_at || "").slice(0, 10)}
                    </span>
                  ) : d.uploaded_at ? (
                    <span className="shrink-0 text-[10px] text-slate-400">Founder · {d.uploaded_at.slice(0, 10)}</span>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button onClick={() => downloadDoc(d)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                    data-testid={`rm-doc-download-${d.doc_id}`}>
                    <Download size={12} /> Download
                  </button>
                  <button onClick={() => deleteDoc(d)} title="Delete this document"
                    className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                    data-testid={`rm-doc-delete-${d.doc_id}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <ClientDocumentUpload companyId={companyId} clientName={clientName}
          onUploaded={(doc) => setData((prev) => ({ ...prev, documents: [doc, ...(prev?.documents || [])] }))} />
      </div>
    </div>
  );
};
